/**
 * PresentationLink — opens the linked design presentation as a fade-in OVERLAY over the
 * journey (no new window), closeable with a ✕ that fades out and returns. Shared by the
 * presentation entry and any milestone entry whose milestone is a presentation-type deliverable.
 *
 * The trigger itself is pluggable: by default renders a "View presentation" button, but a
 * caller can supply `renderTrigger({ onClick })` to render something else (e.g. a JourneyBook
 * 3D cover, matching how DrawingSheetEntry/PreDesignEntry/SpecificationEntry open their own
 * viewers in MilestoneEntry.jsx) while this component still owns the open/close state, the
 * Overlay, and the view-notification side effect below — nothing about opening the
 * presentation changes, only what the client clicks to trigger it.
 *
 * Opening the presentation fires a best-effort Slack ping (notifyPresentationViewed,
 * api/journey-presentation-viewed.js) into the project's own channel every time it's opened —
 * mirrors ProposalReviewEntry.jsx's "View proposal" notification. Suppressed when previewMode is
 * set (the team previewing their own journey must never look like client engagement).
 *
 * Props: { presentation: { recordId, type, milestoneName }, token, label?, previewMode?, renderTrigger? }
 */

import { lazy, Suspense, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { notifyPresentationViewed } from '../../services/journey.js'

const PresentationView = lazy(() => import('../../present/PresentationView.jsx'))

function Overlay({ recordId, type, token, onClose }) {
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true))
    // lock the journey body scroll while the presentation is open
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { cancelAnimationFrame(id); document.body.style.overflow = prev }
  }, [])
  function close() { setEntered(false); setTimeout(onClose, 360) }
  // Portal to <body>: the journey card uses backdrop-filter/transform, which
  // would otherwise trap a position:fixed child inside the card. At body level
  // the overlay fills the whole viewport.
  return createPortal(
    // z-index must clear the team's Preview overlay (JourneyPreviewOverlay.jsx, zIndex 9400) —
    // both portal to document.body as siblings, so a lower value here rendered the presentation
    // invisibly BEHIND the preview's opaque background (button appeared to do nothing). Matches
    // the convention every other in-card viewer already uses (DrawingSetViewer/PdfFlipViewer/
    // RenderingBookViewer all use 9500+).
    <div style={{ position: 'fixed', inset: 0, zIndex: 9500, opacity: entered ? 1 : 0, transition: 'opacity 0.45s ease' }}>
      <Suspense fallback={null}>
        <PresentationView recordId={recordId} type={type} mode="client" token={token} onClose={close} />
      </Suspense>
    </div>,
    document.body
  )
}

export default function PresentationLink({ presentation, token, label = 'View presentation', previewMode = false, renderTrigger }) {
  const [open, setOpen] = useState(false)
  if (!presentation || !token) return null
  function openPresentation() {
    setOpen(true)
    if (!previewMode) notifyPresentationViewed(token, presentation.milestoneName)
  }
  return (
    <>
      {renderTrigger ? renderTrigger({ onClick: openPresentation }) : (
        <button
          type="button"
          onClick={openPresentation}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 22px', borderRadius: 24, cursor: 'pointer',
            background: '#07272D', color: '#eaf2f5', border: 'none',
            fontFamily: "'DM Sans', sans-serif", fontSize: 14, letterSpacing: '0.03em',
          }}
        >
          {label}
        </button>
      )}
      {open ? (
        <Overlay recordId={presentation.recordId} type={presentation.type} token={token} onClose={() => setOpen(false)} />
      ) : null}
    </>
  )
}
